import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Cross,
  Sparkles,
  BookOpen,
  HeartHandshake,
  LineChart,
  ShieldCheck,
  Feather,
  Sun,
  Lock,
  ArrowRight,
} from "lucide-react";

const features = [
  {
    icon: Sparkles,
    title: "Grace, your AI companion",
    text: "A gentle, biblically-grounded counselor — never judging, always pointing back to Christ.",
  },
  {
    icon: Feather,
    title: "Honest sin tracking",
    text: "Quietly name what you struggle with. See patterns. Track the moments you resisted.",
  },
  {
    icon: BookOpen,
    title: "Scripture every day",
    text: "Daily verses, the Ten Commandments, and the teachings of Jesus woven through every page.",
  },
  {
    icon: HeartHandshake,
    title: "Prayer journal",
    text: "Write your prayers. Mark them as answered. Watch how the Lord is moving in your life.",
  },
  {
    icon: LineChart,
    title: "Growth analytics",
    text: "Streaks, weekly graphs, and a spiritual growth score that shows real progress.",
  },
  {
    icon: Lock,
    title: "Sacred privacy",
    text: "Your confessions stay yours. Encrypted at rest. Use anonymously if you wish.",
  },
];

const principles = [
  { ref: "1 John 1:9", text: "If we confess our sins, He is faithful and just to forgive." },
  { ref: "Romans 8:1", text: "There is therefore now no condemnation to those in Christ Jesus." },
  { ref: "Lamentations 3:22-23", text: "His mercies are new every morning." },
];

export default function Landing() {
  const [verse, setVerse] = useState(null);

  useEffect(() => {
    api.get("/bible/daily-verse").then(({ data }) => setVerse(data)).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative celestial-bg overflow-hidden">
        <div className="absolute -top-10 left-1/4 w-64 h-32 cloud-puff animate-cloud-drift pointer-events-none" />
        <div className="absolute top-20 right-12 w-48 h-24 cloud-puff animate-cloud-drift pointer-events-none" style={{ animationDelay: "4s" }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 border border-brand-line backdrop-blur-md px-3 py-1.5 text-xs text-brand-quiet">
              <Sun className="w-3.5 h-3.5 text-brand-gold" strokeWidth={1.8} />
              A peaceful place to walk closer to Christ
            </div>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-brand-ink text-balance leading-[1.05]">
              Where honest hearts
              <br />
              <span className="italic text-brand-skyDark">find quiet grace.</span>
            </h1>
            <p className="mt-6 text-lg text-brand-quiet leading-relaxed max-w-xl">
              Path to Repentance is a private space to track the sins you struggle with,
              receive Bible-rooted counsel from an AI companion, journal your prayers,
              and watch your spirit slowly grow — one day at a time.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/register"
                data-testid="hero-begin-button"
                className="rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-all duration-300 px-7 py-3.5 font-medium inline-flex items-center gap-2 shadow-[0_8px_30px_rgba(123,164,184,0.35)]"
              >
                Begin your journey <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link
                to="/bible"
                data-testid="hero-bible-button"
                className="rounded-full bg-white text-brand-ink border border-brand-line hover:bg-brand-mist transition-colors px-7 py-3.5 font-medium inline-flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4" strokeWidth={1.6} /> Explore teachings
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-6 text-sm text-brand-quiet">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-brand-sage" strokeWidth={1.6} />
                Private & encrypted
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Cross className="w-4 h-4 text-brand-sky" strokeWidth={1.6} />
                Scripture-based
              </span>
              <span className="hidden sm:inline-flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-brand-gold" strokeWidth={1.6} />
                AI companion
              </span>
            </div>
          </div>

          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-brand-sky/10 via-white to-brand-gold/10 rounded-[40px] blur-2xl" />
              <div className="relative rounded-[32px] bg-white border border-brand-line p-8 soft-shadow">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand-quiet">
                  <Sun className="w-4 h-4 text-brand-gold" strokeWidth={1.6} />
                  Verse for today
                </div>
                <p className="mt-5 scripture-quote text-3xl text-brand-ink leading-snug text-balance">
                  “{verse?.text || "His mercies are new every morning; great is Your faithfulness."}”
                </p>
                <p className="mt-4 text-sm text-brand-quiet">
                  — {verse?.reference || "Lamentations 3:22-23"}
                </p>
                <div className="mt-7 grid grid-cols-3 gap-3">
                  <Stat label="Daily verses" value="365" />
                  <Stat label="Commandments" value="10" />
                  <Stat label="Teachings" value="∞" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SCRIPTURE BANNER */}
      <section className="bg-white border-y border-brand-line">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid md:grid-cols-3 gap-8">
          {principles.map((p) => (
            <div key={p.ref} className="flex gap-4">
              <Cross className="w-6 h-6 text-brand-sky mt-1 shrink-0" strokeWidth={1.4} />
              <div>
                <p className="scripture-quote text-xl text-brand-ink leading-snug">“{p.text}”</p>
                <p className="text-xs text-brand-muted mt-1">— {p.ref}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-widest text-brand-quiet">Designed for a sacred journey</p>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-brand-ink tracking-tight">
            Everything a repentant heart needs.
          </h2>
          <p className="mt-4 text-brand-quiet leading-relaxed">
            Six gentle tools, woven together to help you confess, learn, pray, and grow.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group bg-white rounded-2xl border border-brand-line p-7 hover-soft-shadow"
            >
              <span className="inline-flex w-11 h-11 rounded-full bg-brand-mist text-brand-skyDark items-center justify-center group-hover:bg-brand-sky group-hover:text-white transition-colors">
                <f.icon className="w-5 h-5" strokeWidth={1.6} />
              </span>
              <h3 className="mt-5 font-serif text-2xl text-brand-ink">{f.title}</h3>
              <p className="mt-2 text-sm text-brand-quiet leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 lg:px-8 pb-24">
        <div className="max-w-6xl mx-auto rounded-[32px] sun-rays text-white px-8 py-14 lg:px-14 lg:py-20 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 bg-white/15 rounded-full blur-3xl" />
          <div className="absolute -left-10 -bottom-10 w-56 h-56 bg-brand-gold/40 rounded-full blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">
              Take the first quiet step today.
            </h2>
            <p className="mt-4 text-white/85 leading-relaxed">
              No shame. No judgment. Just you, the Bible, and a Companion who walks beside you.
            </p>
            <Link
              to="/register"
              data-testid="cta-register-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-brand-ink hover:bg-brand-mist transition-colors px-7 py-3.5 font-medium"
            >
              Create your private account <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-brand-base border border-brand-line px-3 py-3 text-center">
      <div className="font-serif text-2xl text-brand-ink">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-brand-muted mt-0.5">{label}</div>
    </div>
  );
}
